import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle, Pencil, ClipboardCheck, FileText } from 'lucide-react';

import {
  // VerificationWrapper,
  // VerificationContainer,
  HeaderSection,
  SectionTitle,
  SectionDescription,
  ActionButtons,
  DashboardButton,
  GenerateReportButton,
  ProgressRow,
  ProgressTrack,
  ProgressFill,
  ProgressCount,
  CardGrid,
  Card,
  CardHeader,
  CardTitle,
  EditIconButton,
  StatusPill,
  ConfidenceBadge,
  CardMeta,
  LoadingState,
  EmptyVerificationState,
  RetryButton,
  GlobalError,
} from './verification.styles';

import EditVerificationModal from './components/EditVerificationModal';
import { useVerification } from './hook/Useverification';

const statusLabel = (status) => (status === 'COMPLETE' ? 'Verified' : 'Needs Review');

const Verification = ({ applicationId, onGenerateReport, onDashboard }) => {
  const { verifyMutation, verificationQuery, isComplete, trigger, refetchVerification } =
    useVerification(applicationId);

  const [editingSubmission, setEditingSubmission] = useState(null);
  const [globalError, setGlobalError] = useState('');

  // Kick off verification once, on mount.
  useEffect(() => {
    if (applicationId) trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const submissions = verificationQuery.data?.data ?? [];
  const verifiedCount = submissions.filter((s) => s.verificationStatus === 'COMPLETE').length;
  const allVerified = submissions.length > 0 && verifiedCount === submissions.length;

  const handleGenerateReport = () => {
    if (!allVerified) return;
    onGenerateReport?.();
  };

  const handleRetryVerify = () => {
    setGlobalError('');
    trigger();
  };

  // ── Kicking off verification, waiting on the "Verification Complete!" flag ──
  if (verifyMutation.isPending && !verificationQuery.data) {
    return (
      // <VerificationWrapper>
      <>
        <LoadingState>
          <Loader2 size={22} className="spin" />
          <span>Verifying documents…</span>
        </LoadingState>
      </>
      // </VerificationWrapper>
    );
  }

  // ── Verify call failed outright ──────────────────────────────────────────
  if (verifyMutation.isError && !verificationQuery.data) {
    return (
      // <VerificationWrapper>
      <>
        <EmptyVerificationState>
          <AlertCircle size={36} strokeWidth={1.2} />
          <p style={{ margin: 0, fontWeight: 500 }}>Verification failed</p>
          <p style={{ margin: 0, fontSize: 13 }}>
            {verifyMutation.error?.message || 'Something went wrong. Please try again.'}
          </p>
          <RetryButton type="button" onClick={handleRetryVerify}>
            <ClipboardCheck size={14} /> Try Again
          </RetryButton>
        </EmptyVerificationState>
      </>
      // </VerificationWrapper>
    );
  }

  // ── Verify call succeeded but not "Verification Complete!" yet ──────────
  if (!isComplete && !verificationQuery.data) {
    return (
      // <VerificationWrapper>
      <>
        <EmptyVerificationState>
          <Loader2 size={36} strokeWidth={1.2} className="spin" />
          <p style={{ margin: 0, fontWeight: 500 }}>Verification In Progress</p>
          <p style={{ margin: 0, fontSize: 13 }}>
            Your documents are currently being verified. Check back shortly or try again.
          </p>
          <RetryButton
            type="button"
            onClick={handleRetryVerify}
            disabled={verifyMutation.isPending}
          >
            {verifyMutation.isPending ? (
              <>
                <Loader2 size={14} className="spin" /> Checking…
              </>
            ) : (
              <>
                <ClipboardCheck size={14} /> Check Again
              </>
            )}
          </RetryButton>
        </EmptyVerificationState>
      </>
      // </VerificationWrapper>
    );
  }

  // ── Fetching the actual verification data ────────────────────────────────
  if (verificationQuery.isLoading) {
    return (
      // <VerificationWrapper>
      <>
        <LoadingState>
          <Loader2 size={22} className="spin" />
          <span>Loading verification results…</span>
        </LoadingState>
      </>
      // </VerificationWrapper>
    );
  }

  if (verificationQuery.isError) {
    return (
      // <VerificationWrapper>
      <>
        <GlobalError role="alert">
          <AlertCircle size={15} />
          {verificationQuery.error?.message || 'Failed to load verification data.'}
        </GlobalError>
      </>
      // </VerificationWrapper>
    );
  }

  // ── Main card-grid view ───────────────────────────────────────────────────
  return (
    // <VerificationWrapper>
    <>
      <HeaderSection>
        <div>
          <SectionTitle>Verify Extracted Data</SectionTitle>
          <SectionDescription>
            Check each document below and correct anything that was matched incorrectly before
            generating the report.
          </SectionDescription>
        </div>
        <ActionButtons>
          <DashboardButton onClick={onDashboard} type="button">
            Dashboard
          </DashboardButton>
          <GenerateReportButton
            type="button"
            disabled={!allVerified}
            onClick={handleGenerateReport}
          >
            Results
          </GenerateReportButton>
        </ActionButtons>
      </HeaderSection>

      {globalError && (
        <GlobalError role="alert">
          <AlertCircle size={15} />
          {globalError}
        </GlobalError>
      )}

      <ProgressRow>
        <ProgressTrack>
          <ProgressFill $pct={(verifiedCount / (submissions.length || 1)) * 100} />
        </ProgressTrack>
        <ProgressCount>
          {verifiedCount} / {submissions.length} verified
        </ProgressCount>
      </ProgressRow>

      {submissions.length === 0 ? (
        <EmptyVerificationState>
          <FileText size={36} strokeWidth={1.2} />
          <p style={{ margin: 0 }}>No documents to verify yet.</p>
        </EmptyVerificationState>
      ) : (
        <CardGrid>
          {submissions.map((sub) => {
            const isVerified = sub.verificationStatus === 'COMPLETE';
            const confidence = sub.verificationResponse?.confidenceScore;
            const reason = sub.verificationResponse?.reason;
            return (
              <Card key={sub._id}>
                <CardHeader>
                  <div>
                    <CardTitle>{sub.name}</CardTitle>
                  </div>
                  <EditIconButton
                    type="button"
                    aria-label={`Edit ${sub.name}`}
                    onClick={() => setEditingSubmission(sub)}
                  >
                    <Pencil size={14} />
                  </EditIconButton>
                </CardHeader>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <StatusPill $status={sub.verificationStatus}>
                    {statusLabel(sub.verificationStatus)}
                  </StatusPill>
                  {typeof confidence === 'number' && (
                    <ConfidenceBadge $low={confidence < 90}>
                      {confidence}% match confidence
                    </ConfidenceBadge>
                  )}
                </div>

                {reason && <CardMeta>{reason}</CardMeta>}
              </Card>
            );
          })}
        </CardGrid>
      )}

      {editingSubmission && (
        <EditVerificationModal
          applicationId={applicationId}
          submission={editingSubmission}
          onClose={() => setEditingSubmission(null)}
          onSaved={refetchVerification}
        />
      )}
    </>
    // </VerificationWrapper>
  );
};

export default Verification;
